<template>
  <span class="base-form-select" :class="{ 'is-invalid ': isInvalid }">
    <select
      v-model="currentValue"
      class="base-form-select__field"
      :disabled="disabled"
      :required="required"
      v-bind="$attrs"
      @change="handleChange($event)"
      @invalid="isValid(false)"
      @valid="isValid(true)"
    >
      <option :value="defaultValue" :disabled="required">
        {{ defaultPlaceholder }}
      </option>
      <slot />
      <option
        v-for="(option, key) in options"
        :key="key"
        :value="option[optionValue]"
        :selected="option[optionValue] === currentValue"
      >
        {{ option[optionText] }}
      </option>
      <slot name="append-option" />
    </select>
    <span class="base-form-select-wrap__icon">
      <SvgIcon name="chevron-down" size="sm" />
    </span>
  </span>
</template>

<script>
import Vue from 'vue';
import SvgIcon from './SvgIcon.vue';

export default {
  name: 'BaseFormSelect',

  components: {
    SvgIcon
  },

  inheritAttrs: false,

  props: {
    options: {
      type: Array,
      default: () => []
    },
    defaultPlaceholder: {
      type: String,
      default: () => Vue.t('utils:common.defaultSelectPlaceholder')
    },
    defaultValue: {
      type: [String, Number, Boolean],
      default: ''
    },
    optionValue: {
      type: String,
      default: 'id'
    },
    optionText: {
      type: String,
      default: 'name'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Number, Boolean],
      default: null
    },
    required: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isInvalid: false,
      currentValue: this.value ?? ''
    };
  },

  watch: {
    currentValue(val) {
      this.$emit('input', val);
    }
  },

  methods: {
    isValid(valid) {
      this.isInvalid = !valid;
    },
    handleChange(event) {
      const val = event?.target?.value || null;

      if (val) {
        this.isValid(true);
      }
    }
  }
};
</script>

<style lang="scss">
.base-form-select {
  position: relative;
  display: block;

  // remove additional styles added by form-control in client-dashboard until we can remove that class from the baseFormSelect in that codebase
  padding: 0;
  border: none;
  background: color(form-default-input-background);

  ::placeholder {
    color: color(form-default-placeholder-text);
  }
}

.base-form-select-wrap__icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: spacing('4x');
  pointer-events: none;

  svg {
    color: color(form-default-input-lines);

    .is-invalid &,
    .has-error & {
      color: color(form-error-message-text);
    }
  }
}

.base-form-select__field {
  @include font-size(md);
  color: color(form-default-input-text);
  appearance: none;
  display: block;
  width: 100%;
  background-color: #fff;
  background-image: none;
  border-radius: rounding('1x');
  border: 1px color(form-default-input-lines) solid;
  padding: spacing('2x') spacing('4x');
  height: auto;
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;

  &:not([multiple]) {
    padding-right: 2.5rem;

    // Hide the default arrow in IE10 and IE11
    &::-ms-expand {
      display: none;
    }
  }

  &[disabled],
  &[readonly] {
    background-color: color(form-disabled-input-background);
    border: 1px solid color(form-disabled-input-lines);
    color: color(form-disabled-input-text);
  }

  .is-invalid &,
  .has-error & {
    border-color: color(form-error-input-lines);
  }

  @include generate-form-selecting-states;

  &:hover {
    .is-invalid &,
    .has-error & {
      border-color: color(form-error-input-lines);
      box-shadow: get-form-selecting-box-shadow(error);
    }
  }

  &:focus-visible {
    .is-invalid &,
    .has-error & {
      @include generate-form-elements-focus();
    }
  }

  &:focus-visible,
  &:hover {
    &[disabled],
    &[readonly] {
      box-shadow: none;
      border-color: color(form-disabled-input-lines);
      cursor: not-allowed;
    }
  }
}
</style>
